import { useState } from 'react';

import MergeIcon from '@mui/icons-material/Merge';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { AsyncAutocomplete } from 'components';
import { ColumnIds } from 'enums';
import { useDialogStatus } from 'hooks/common';

import {
  getEntityDisplayName,
  getEntityEndpoint,
  getMergeEndpoint,
} from './configurations';
import { EntityType, MergeableEntity } from './types';
import { GenericMergeDialog } from './GenericMergeDialog';
import axios from 'axios';

const ENTITY_SEARCH_DYNAMIC = [
  { columnId: ColumnIds.Name, type: 'string' },
  { columnId: ColumnIds.Id, type: 'number' },
];

const ENTITY_TYPE_OPTIONS: EntityType[] = [
  // 'organizations',
  // 'competitions',
  // 'seasons',
  // 'rounds',
  // 'fixtures',
  // 'persons',
  'players',
  // 'doubles',
  // 'teams',
  // 'horses',
  'venues',
];

export const Merge = () => {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType | undefined>(undefined);
  const [firstEntity, setFirstEntity] = useState<MergeableEntity | null>(null);
  const [secondEntity, setSecondEntity] = useState<MergeableEntity | null>(null);

  const {
    isOpen: isMergeDialogOpen,
    onShow: onShowMergeDialog,
    onHide: onHideMergeDialog,
  } = useDialogStatus();

  const handleEntityTypeChange = (event: SelectChangeEvent<string>) => {
    const newEntityType = event.target.value as EntityType;
    setSelectedEntityType(newEntityType);
    setFirstEntity(null);
    setSecondEntity(null);
  };

  const handleFirstEntityChange = (entity: MergeableEntity | null) => {
    setFirstEntity(entity);
  };

  const handleSecondEntityChange = (entity: MergeableEntity | null) => {
    setSecondEntity(entity);
  };

  const handleMergeClick = () => {
    if (firstEntity && secondEntity && selectedEntityType) {
      onShowMergeDialog();
    }
  };

  const handleConfirmMerge = async (mergeData: { realId: number; fakeId: number }) => {
    try {
      if (!selectedEntityType) {
        return;
      }

      await axios.post(getMergeEndpoint(selectedEntityType), {
        originalId: mergeData.realId,
        duplicateId: mergeData.fakeId,
      });

      setFirstEntity(null);
      setSecondEntity(null);
      onHideMergeDialog();
    } catch (error) {
      console.error('Error merging entities:', error);
    }
  };

  const isMergeButtonDisabled = !firstEntity || !secondEntity || !selectedEntityType;
  const selectedEntities = firstEntity && secondEntity ? [firstEntity, secondEntity] : [];

  return (
    <>
      <GenericMergeDialog
        open={isMergeDialogOpen}
        entities={selectedEntities}
        entityType={selectedEntityType as EntityType}
        onClose={onHideMergeDialog}
        onConfirm={handleConfirmMerge}
      />

      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Merge Entities
        </Typography>

        <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
          Select the type of entity you want to merge, then choose the two entities to merge.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="entity-type-label">Entity Type</InputLabel>
              <Select
                labelId="entity-type-label"
                id="entity-type-select"
                value={selectedEntityType || ''}
                label="Entity Type"
                onChange={handleEntityTypeChange}
                data-testid="entity-type-select"
              >
                {ENTITY_TYPE_OPTIONS.map(entityType => (
                  <MenuItem key={entityType} value={entityType}>
                    {getEntityDisplayName(entityType)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedEntityType && (
            <>
              <Grid item xs={12} md={6}>
                <AsyncAutocomplete
                  label={`First ${getEntityDisplayName(selectedEntityType)}`}
                  dataSource={getEntityEndpoint(selectedEntityType)}
                  onChange={handleFirstEntityChange}
                  defaultValue={firstEntity}
                  searchTermMinLength={1}
                  dynamicSearch={ENTITY_SEARCH_DYNAMIC}
                  textFieldProps={{
                    'data-testid': 'first-entity-autocomplete',
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <AsyncAutocomplete
                  label={`Second ${getEntityDisplayName(selectedEntityType)}`}
                  dataSource={getEntityEndpoint(selectedEntityType)}
                  onChange={handleSecondEntityChange}
                  defaultValue={secondEntity}
                  searchTermMinLength={1}
                  dynamicSearch={ENTITY_SEARCH_DYNAMIC}
                  textFieldProps={{
                    'data-testid': 'second-entity-autocomplete',
                  }}
                  required
                />
              </Grid>
            </>
          )}

          {selectedEntityType && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={isMergeButtonDisabled}
                  onClick={handleMergeClick}
                  startIcon={<MergeIcon />}
                  size="large"
                >
                  Merge {getEntityDisplayName(selectedEntityType)}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};
